import { getProperty } from '@lib/api-services/propertiesService'
import { getAgentBySlug } from '@lib/api-services/agentsService'
import { getWagtailPageBySlug } from '@lib/api-services/cmsPageService'
import EnquiryForm from '@components/global/Form/EnquiryForm'
import PropertyDetailsEnquiryHeader from '@components/PropertyDetails/EnquiryHeader'
import { propertyChannelString, propertyDetailsUrl, stripTags } from '@lib/helpers'
import { Container } from '@styles/global.styled'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import * as S from '../styles/enquiry.styled'

const BRAND = 'Barry Plant'
const META_DESC_MAX = 158

const truncateMeta = (text, max = META_DESC_MAX) => {
  if (!text || typeof text !== 'string') return ''
  const t = text.replace(/\s+/g, ' ').trim()
  if (t.length <= max) return t
  return `${t.slice(0, max - 1).trimEnd()}…`
}

const listingAddressLine = (listing) =>
  listing.address_display_string ||
  [
    listing.address_street_display,
    listing.address_suburb,
    listing.address_state,
    listing.address_postcode,
  ]
    .filter(Boolean)
    .join(' ') ||
  listing.address_suburb ||
  'Property'

const listingOgImage = (listing) => {
  const img = listing?.images?.[0]?.image
  if (!img) return null
  return img.image_800_600 || img.image || null
}

const EnquirySeo = ({ enquiryData, isThankyouPage }) => {
  let title = `Enquiry | ${BRAND}`
  let description = `Send an enquiry to ${BRAND}. We will respond as soon as we can.`
  let ogImage = null

  if (enquiryData.type === 'listing' && enquiryData.listing) {
    const listing = enquiryData.listing
    const address = listingAddressLine(listing)
    const channel = propertyChannelString(listing)
    const headline = stripTags(listing.headline || '')
    title = isThankyouPage
      ? `Thank you — enquiry sent | ${BRAND}`
      : `Enquire about ${address} (${channel}) | ${BRAND}`
    description = truncateMeta(
      `Send a property enquiry for this ${channel.toLowerCase()} listing at ${address}. ${headline || `Contact ${BRAND} about this property.`
      }`
    )
    ogImage = listingOgImage(listing)
  } else if (enquiryData.type === 'agent' && enquiryData.headerInfo) {
    const { title: agentName, description: jobTitle } = enquiryData.headerInfo
    title = isThankyouPage
      ? `Thank you — enquiry sent | ${BRAND}`
      : `Contact ${agentName}${jobTitle ? `, ${jobTitle}` : ''} | ${BRAND}`
    description = truncateMeta(
      `Send an enquiry to ${agentName}${jobTitle ? ` (${jobTitle})` : ''
      } at ${BRAND}. We will get back to you shortly.`
    )
    ogImage = enquiryData.headerInfo.image || null
  } else if (enquiryData.type === 'office' && enquiryData.headerInfo) {
    const officeTitle = enquiryData.headerInfo.title
    const location = enquiryData.headerInfo.description
    title = isThankyouPage
      ? `Thank you — enquiry sent | ${BRAND}`
      : `Contact ${officeTitle} | ${BRAND}`
    description = truncateMeta(
      `Send an enquiry to ${officeTitle}${location ? ` — ${location}` : ''
      }. The ${BRAND} team will respond as soon as possible.`
    )
    ogImage = enquiryData.headerInfo.image || null
  }

  const openGraph = {
    type: 'website',
    locale: 'en_AU',
    siteName: BRAND,
    title,
    description,
    ...(ogImage
      ? {
        images: [
          {
            url: ogImage,
            alt: title,
            width: 1200,
            height: 630,
          },
        ],
      }
      : {}),
  }

  return (
    <NextSeo
      title={title}
      description={description}
      noindex={isThankyouPage}
      openGraph={openGraph}
    />
  )
}

const EnquiryPage = ({ enquiryData, error, referrerUrl = '/' }) => {
  const router = useRouter()
  const isThankyouPage = router.asPath?.includes('/thankyou')

  if (error) {
    return (
      <Container>
        <NextSeo
          title={`Enquiry unavailable | ${BRAND}`}
          description={truncateMeta(error)}
          noindex
        />
        <S.ErrorContainer>
          <h1>Error</h1>
          <p>{error}</p>
        </S.ErrorContainer>
      </Container>
    )
  }

  if (!enquiryData) {
    return (
      <Container>
        <NextSeo
          title={`Enquiry unavailable | ${BRAND}`}
          description='The enquiry form could not be loaded.'
          noindex
        />
        <S.ErrorContainer>
          <h1>Error</h1>
          <p>Unable to load enquiry form</p>
        </S.ErrorContainer>
      </Container>
    )
  }

  // Render customHeader for property listings
  const customHeader =
    enquiryData.type === 'listing' && enquiryData.listing ? (
      <PropertyDetailsEnquiryHeader listing={enquiryData.listing} />
    ) : null

  // Generate success message based on enquiry type
  const getSuccessMessage = () => {
    if (enquiryData.type === 'listing' && enquiryData.listing) {
      return (
        <>
          {enquiryData.listing?.address_street_display ? (
            <>
              {enquiryData.listing?.address_street_display}
              <br />
            </>
          ) : (
            ''
          )}
          {enquiryData.listing?.address_suburb}{' '}
          {enquiryData.listing?.address_state}{' '}
          {enquiryData.listing?.address_postcode}
        </>
      )
    } else if (enquiryData.type === 'agent' && enquiryData.headerInfo) {
      return (
        <>
          {enquiryData.headerInfo.title}
          <br />
          {enquiryData.headerInfo.description}
        </>
      )
    } else if (enquiryData.type === 'office' && enquiryData.headerInfo) {
      return enquiryData.headerInfo.title
    }
    return null
  }

  let successMessage = null

  // If thankyou page, show success message
  if (isThankyouPage) {
    successMessage = getSuccessMessage()
  }

  return (
    <>
      <EnquirySeo enquiryData={enquiryData} isThankyouPage={isThankyouPage} />
      <S.GlobalStyles />
      <EnquiryForm
        standalone
        {...enquiryData}
        customHeader={customHeader}
        referrerUrl={referrerUrl}
        showSuccessMessage={!!successMessage}
        successMessage={successMessage}
      />
    </>
  )
}

EnquiryPage.getInitialProps = async (ctx) => {
  const { query, res } = ctx
  const { type, id, slug, agentSlug } = query

  try {
    // Property enquiry - route: /:type/:slug-:id/enquiry/
    if (id && type) {
      const propertyData = await getProperty(id)
      if (!propertyData || !propertyData.id) {
        if (res) {
          res.statusCode = 404
        }
        return {
          error: 'Property not found',
          statusCode: 404,
        }
      }
      const referrerUrl = propertyDetailsUrl(propertyData) || '/'
      return {
        enquiryData: {
          type: 'listing',
          listing: propertyData,
        },
        referrerUrl,
      }
    }
    // Agent enquiry - route: /offices/:slug/our-team/:agentSlug/enquiry/ or /our-team/:agentSlug/enquiry/
    else if (agentSlug) {
      const agentData = await getAgentBySlug(agentSlug)
      if (!agentData || !agentData.id) {
        if (res) {
          res.statusCode = 404
        }
        return {
          error: 'Agent not found',
          statusCode: 404,
        }
      }
      const referrerUrl = !slug ? `/our-team/` : `/offices/${slug}/our-team/${agentSlug}/`
      return {
        enquiryData: {
          type: 'agent',
          agentName: agentData.title,
          agentSlug: agentData.meta?.slug,
          officeSlug: slug,
          headerInfo: {
            image: agentData.profile_image?.url,
            title: agentData.title,
            description: agentData.job_title,
            id: agentData.id,
          },
        },
        referrerUrl,
      }
    }
    // Office enquiry - route: /offices/:slug/enquiry/
    else if (slug && !agentSlug) {
      const { pageData } = await getWagtailPageBySlug(`/offices/${slug}/`)
      if (!pageData || !pageData.id) {
        if (res) {
          res.statusCode = 404
        }
        return {
          error: 'Office not found',
          statusCode: 404,
        }
      }
      const officeName = pageData.office_name
      const pageTitle =
        pageData.custom_link_title || `Barry Plant ${officeName}`
      const address = [
        pageData.address,
        pageData.suburb,
        pageData.state,
        pageData.postcode,
      ]
        .filter(Boolean)
        .join(' ')

      const referrerUrl = `/offices/${slug}/`
      return {
        enquiryData: {
          type: 'office',
          officePageTitle: pageTitle,
          officeSlug: slug,
          headerInfo: {
            image: pageData.office_image?.url,
            title: pageTitle,
            description: address,
            id: pageData.id,
          },
        },
        referrerUrl,
      }
    } else {
      if (res) {
        res.statusCode = 404
      }
      return {
        error: 'Invalid enquiry parameters',
        statusCode: 404,
      }
    }
  } catch (err) {
    console.error('Error fetching enquiry data:', err)
    if (res) {
      res.statusCode = 500
    }
    return {
      error: 'Failed to load enquiry form',
      statusCode: 500,
    }
  }
}

export default EnquiryPage
